import useProcess from '@hooks/useProcess';
import useStores from '@hooks/useStore';
import { hasElevatedRights, type StudioUser } from '@stratumn/studio-shared';
import { Loader2 } from 'lucide-react';
import { observer } from 'mobx-react';
import { useMemo, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { LoaderBox } from '@/components/ui/loader';
import { ParamsList } from '@/routes/routes.types';
import { DangerZoneSection } from './components/DangerZoneSection';
import { EnvOverridesSection } from './components/EnvOverridesSection';
import { GeneralSection } from './components/GeneralSection';
import { GenerationsSection } from './components/GenerationsSection';
import { WorkflowsSection } from './components/WorkflowsSection';
import { WorkspaceDetailHeader } from './components/WorkspaceDetailHeader';
import type { WorkspaceFormValues } from './form/form.workspace';
import { useWorkspaceDetail } from './hooks/useWorkspaceDetail';

export const WorkspaceDetailPage = observer(() => {
  const process = useProcess();
  const outletContext = useOutletContext<{ user: StudioUser } | undefined>();
  const { workspaceStore } = useStores();
  const navigate = useNavigate();
  const processId = useParams()[ParamsList.ProcessId] as string;
  const { workspace, loading } = useWorkspaceDetail(processId);

  const isPrivileged = hasElevatedRights(outletContext?.user);
  const currentUserId = outletContext?.user?.rowId;

  const [isEditing, setIsEditing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Process workflows are the source of truth for which workflows exist.
  // The workspace's published_workflow_ids map stores workspace-specific Trace IDs.
  const processWorkflows = process?.workflows ?? [];

  const workflowNames = useMemo(() => {
    const names: Record<string, string> = {};
    for (const wf of processWorkflows) {
      names[wf.id] = wf.name;
    }
    return names;
  }, [processWorkflows]);

  if (loading || !workspace) {
    return (
      <LoaderBox>
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </LoaderBox>
    );
  }

  const publishedIds = workspace.published_workflow_ids ?? {};

  // A workspace can be deleted by a privileged user or by the user who created
  // it, mirroring the backend WorkspaceDeletePermissionGuard. The null guard on
  // currentUserId avoids matching legacy rows whose createdBy is null.
  const canDelete =
    isPrivileged ||
    (currentUserId != null && workspace.createdBy === currentUserId);

  const handleUpdate = async (values: WorkspaceFormValues) => {
    try {
      await workspaceStore.updateWorkspace(processId, workspace.id, {
        name: values.name.trim(),
        color: values.color,
        version: values.version
      });
      toast.success('Workspace updated');
      setIsEditing(false);
      await workspaceStore.fetchWorkspace(processId, workspace.id);
    } catch {
      toast.error('Failed to update workspace');
    }
  };

  const handlePublishedIdSave = async (
    workflowId: string,
    publishedId: string | null
  ) => {
    try {
      await workspaceStore.adminUpdatePublishedWorkflowIds(
        processId,
        workspace.id,
        {
          published_workflow_ids: publishedId
            ? { [workflowId]: publishedId }
            : { [workflowId]: '' }
        }
      );
      toast.success('Published ID updated');
    } catch {
      toast.error('Failed to update published ID');
    }
  };

  const onDelete = async () => {
    try {
      await workspaceStore.deleteWorkspace(processId, workspace.id);
      toast.success(`Workspace "${workspace.name}" deleted`);
      navigate(`/${processId}/workspaces`);
    } catch {
      toast.error('Failed to delete workspace');
    }
  };

  return (
    <div className="flex size-full flex-col overflow-auto bg-[#fafafa] [scrollbar-gutter:stable]">
      <WorkspaceDetailHeader
        color={workspace.color || '#6366f1'}
        name={workspace.name}
        onBack={() => navigate(`/${processId}/workspaces`)}
      />

      <div className="mx-auto flex w-full max-w-7xl flex-col gap-8 p-8 pb-16">
        <GeneralSection
          isEditing={isEditing}
          onCancelEditing={() => setIsEditing(false)}
          onStartEditing={() => setIsEditing(true)}
          onSubmit={handleUpdate}
          processId={processId}
          workspace={workspace}
        />

        <WorkflowsSection
          isPrivileged={isPrivileged}
          onPreAllocate={() =>
            workspaceStore.preAllocateWorkflowIds(processId, workspace.id)
          }
          onSavePublishedId={handlePublishedIdSave}
          processWorkflows={processWorkflows}
          publishedIds={publishedIds}
        />

        <EnvOverridesSection processId={processId} workspaceId={workspace.id} />

        <GenerationsSection processId={processId} workspaceId={workspace.id} />

        {canDelete && (
          <DangerZoneSection
            isOpen={deleteDialogOpen}
            onClose={() => setDeleteDialogOpen(false)}
            onConfirmDelete={onDelete}
            onOpen={() => setDeleteDialogOpen(true)}
            workflowNames={workflowNames}
            workspace={workspace}
          />
        )}
      </div>
    </div>
  );
});
